// ============================================================================
// Pig daily report: the three SOWS sections (owner request 2026-11-04)
// ============================================================================
//
// When SOWS is picked, the form shows Sow Group 1, 2 and 3, each with its own
// feed, pig count, fence voltage, the five yes/no checks and notes, and
// submits ONE pig_dailys row per group, labelled 'SOWS · Group n' (see
// src/lib/pigSowGroups.ts for why the labels differ). All three groups are
// always shown and always required, including a group with no tagged sows.
//
// The section answers live inside the pig form itself (`pForm.sowGroups`), so
// they travel in the hub draft snapshot and a reload keeps them. The key is
// absent until a section is first edited, so an untouched SOWS form compares
// equal to its fresh copy.
// ============================================================================

import {SOWS_GROUP_VALUE, SOW_GROUP_NUMBERS, sowGroupBatchId, sowGroupLabel} from '../lib/pigSowGroups.js';
import type {SowGroupNumber} from '../lib/pigSowGroups.js';

/**
 * True when the pig form shows the three sow sections: SOWS is picked and the
 * form is not replacing a recovered queued row (a recovered row keeps the
 * one-row form so it is replaced exactly as queued).
 */
export function isSowsSectionMode(batchLabel: unknown, recoveringQueuedPigRow: boolean = false): boolean {
  return (
    !recoveringQueuedPigRow &&
    String(batchLabel ?? '')
      .trim()
      .toUpperCase() === SOWS_GROUP_VALUE
  );
}

export type SowSection = {
  feedLbs: string;
  pigCount: string;
  fenceVoltage: string;
  groupMoved: boolean | null;
  nippleDrinkerMoved: boolean;
  nippleDrinkerWorking: boolean;
  troughsMoved: boolean;
  fenceWalked: boolean;
  issues: string;
};

/** A fresh section: "Was group moved?" blank, the other four checks at their usual Yes default. */
export const SOW_SECTION_DEFAULTS: Readonly<SowSection> = Object.freeze({
  feedLbs: '',
  pigCount: '',
  fenceVoltage: '',
  groupMoved: null,
  nippleDrinkerMoved: true,
  nippleDrinkerWorking: true,
  troughsMoved: true,
  fenceWalked: true,
  issues: '',
});

function text(v: unknown): string {
  return v == null ? '' : String(v);
}

/** The three sections of a pig form, filling any missing section or field with the defaults. */
export function sowSectionsOf(form: {readonly [key: string]: unknown} | null | undefined): SowSection[] {
  const stored = Array.isArray(form?.sowGroups) ? (form?.sowGroups as unknown[]) : [];
  return SOW_GROUP_NUMBERS.map((_, i) => {
    const s = (stored[i] && typeof stored[i] === 'object' ? stored[i] : {}) as Partial<
      Record<keyof SowSection, unknown>
    >;
    return {
      feedLbs: text(s.feedLbs ?? SOW_SECTION_DEFAULTS.feedLbs),
      pigCount: text(s.pigCount ?? SOW_SECTION_DEFAULTS.pigCount),
      fenceVoltage: text(s.fenceVoltage ?? SOW_SECTION_DEFAULTS.fenceVoltage),
      groupMoved: s.groupMoved === true || s.groupMoved === false ? s.groupMoved : null,
      nippleDrinkerMoved: s.nippleDrinkerMoved !== false,
      nippleDrinkerWorking: s.nippleDrinkerWorking !== false,
      troughsMoved: s.troughsMoved !== false,
      fenceWalked: s.fenceWalked !== false,
      issues: text(s.issues ?? SOW_SECTION_DEFAULTS.issues),
    };
  });
}

/** The same sections with one field of one section changed (immutable). */
export function withSowSectionField<K extends keyof SowSection>(
  form: {readonly [key: string]: unknown} | null | undefined,
  index: number,
  field: K,
  value: SowSection[K],
): SowSection[] {
  return sowSectionsOf(form).map((s, i) => (i === index ? {...s, [field]: value} : s));
}

/** The admin-config field values of one section, for validateRequiredFields. */
export function sowSectionFieldValues(
  date: string,
  teamMember: string,
  n: SowGroupNumber,
  s: SowSection,
): Record<string, unknown> {
  return {
    date,
    team_member: teamMember,
    batch_label: sowGroupLabel(n),
    feed_lbs: s.feedLbs,
    pig_count: s.pigCount,
    fence_voltage: s.fenceVoltage,
    group_moved: s.groupMoved,
    nipple_drinker_moved: s.nippleDrinkerMoved,
    nipple_drinker_working: s.nippleDrinkerWorking,
    troughs_moved: s.troughsMoved,
    fence_walked: s.fenceWalked,
    issues: s.issues,
  };
}

export type SowRowPayload = {
  date: string;
  team_member: string;
  batch_id: string;
  batch_label: string;
  pig_count: number | null;
  feed_lbs: number | null;
  group_moved: boolean;
  nipple_drinker_moved: boolean;
  nipple_drinker_working: boolean;
  troughs_moved: boolean;
  fence_walked: boolean;
  fence_voltage: number | null;
  issues: string | null;
};

/**
 * One pig_dailys payload per sow group, in group order, with the single-row
 * path's column shape. Callers validate first (group_moved must already be an
 * explicit answer); the payload carries `=== true` so a blank can never reach
 * the server's default-to-Yes.
 */
export function buildSowRowPayloads(
  date: string,
  teamMember: string,
  sections: readonly SowSection[],
): SowRowPayload[] {
  return SOW_GROUP_NUMBERS.map((n, i) => {
    const s = sections[i] ?? SOW_SECTION_DEFAULTS;
    return {
      date,
      team_member: teamMember,
      batch_id: sowGroupBatchId(n),
      batch_label: sowGroupLabel(n),
      pig_count: s.pigCount !== '' ? parseInt(s.pigCount) : null,
      feed_lbs: s.feedLbs !== '' ? parseFloat(s.feedLbs) : null,
      group_moved: s.groupMoved === true,
      nipple_drinker_moved: s.nippleDrinkerMoved,
      nipple_drinker_working: s.nippleDrinkerWorking,
      troughs_moved: s.troughsMoved,
      fence_walked: s.fenceWalked,
      fence_voltage: s.fenceVoltage !== '' ? parseFloat(s.fenceVoltage) : null,
      issues: s.issues || null,
    };
  });
}

export type SowRowOutcome = {label: string; state: 'synced' | 'queued' | 'stuck' | 'updated' | 'failed' | 'not-sent'};

/** The combined state of the three rows: stuck beats queued beats synced. */
export function combinedSowState(outcomes: readonly SowRowOutcome[]): 'synced' | 'queued' | 'stuck' {
  if (outcomes.some((o) => o.state === 'stuck')) return 'stuck';
  if (outcomes.some((o) => o.state === 'queued')) return 'queued';
  return 'synced';
}

/** The message for a submit that stopped part-way, naming every group by what happened to it. */
export function sowPartialMessage(outcomes: readonly SowRowOutcome[], reason: string): string {
  const named = (states: SowRowOutcome['state'][]) =>
    outcomes.filter((o) => states.includes(o.state)).map((o) => o.label);
  const saved = named(['synced', 'updated']);
  const queued = named(['queued', 'stuck']);
  const notSaved = named(['failed', 'not-sent']);
  const parts: string[] = [];
  if (saved.length) parts.push(`Saved: ${saved.join(', ')}.`);
  if (queued.length) parts.push(`Saved on this device, will send: ${queued.join(', ')}.`);
  if (notSaved.length) parts.push(`NOT saved: ${notSaved.join(', ')} (${reason}).`);
  parts.push('Submit again to send the rest; groups already saved will show as duplicates.');
  return parts.join(' ');
}

/** Refuse SOWS as an additional group in multi-row pig daily submissions. */
export function extraGroupsSowsRefusal(extraGroups: ReadonlyArray<Record<string, any>>): string | null {
  if (
    extraGroups.some(
      (g) =>
        String(g.batchLabel || '')
          .trim()
          .toUpperCase() === 'SOWS',
    )
  ) {
    return 'Pick SOWS as the main group to report its three groups.';
  }
  return null;
}
